import { addOrRemove, Button, Flex, Input, isNullish, Menu } from "@theorgchartdev/storybook";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { FormatValueFromFieldName, GetUniqueLocalValuesForField } from "@/Utils/DataLib";
import { GetDoc } from "@/Utils/StateLib";
import { GetValuesForField } from "@/Utils/Promises";
import {
  InputComponentDict,
  NormalizeFieldValue
} from "../../FieldInputValuePickerComposite/FieldInputValuePickerComposite";
import { useAsyncEffect } from "@/hooks/useAsyncEffect";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { enumFieldType, enumObjClassNames } from "@coreapp/constants";
import { isBlockedField, getBlockedFieldDisplayValue } from "@coreapp/utils/FormatFieldValue";
import DateRangeComponentField from "../../ReactInputFields/DateRangeInputField/DateRangeInputField";

export const ValueModeEnum = {
  NoValue: 0,
  SingleSelect: 1,
  MultiSelect: 2,
  UserEntry: 3,
  Range: 4,
  DateRange: 5
};

export const getValueMode = rule => {
  const comparison = rule.GetComparison();
  switch (comparison) {
    case "isempty":
    case "isnotempty":
      return ValueModeEnum.NoValue;
    case "greater":
    case "less":
    case "greaterequal":
    case "lessequal":
      return ValueModeEnum.SingleSelect;
    case "inlist":
    case "notinlist":
      return ValueModeEnum.MultiSelect;
    case "startswith":
    case "contains":
    case "doesnotcontain":
      return ValueModeEnum.UserEntry;
    case "range":
      return ValueModeEnum.Range;
    case "daterange":
      return ValueModeEnum.DateRange;
  }

  return ValueModeEnum.SingleSelect;
};

export const RulePairValueInput = props => {
  const doc = GetDoc();
  const chartID = doc?.GetChartID();
  const popoverTestIdPrefix = props.popoverTestIdPrefix ?? "rule-input-menu";

  const field = props.rulePair.GetFieldName();
  const fieldClassName = props.rulePair.GetFieldClassName() ?? enumObjClassNames.box;
  const fieldDef = props.rulePair.GetReferenceFieldDef(doc);
  const fieldType = fieldDef?.GetFieldType();
  const valueString = props.rulePair.GetValueDisplayString(doc);
  const value = props.rulePair.GetValue();
  const valueMode = getValueMode(props.rulePair);

  const { loading, data } = useAsyncEffect(async () => {
    if (!field) {
      return [];
    }

    let results = [];
    let fetchedChartID = props.chartData?.ID ?? chartID;

    if (doc?.IsServerChart() || props.chartData?.isArchive) {
      const response = await GetValuesForField(
        field,
        fetchedChartID,
        props.chartData?.isArchive ?? false
      );
      results = response.results;
    } else {
      results = GetUniqueLocalValuesForField(field, fieldClassName);
    }

    return results;
  }, [chartID, field, props.chartData?.ID, props.chartData?.isArchive, fieldClassName]);

  if (valueMode === ValueModeEnum.NoValue) {
    return null;
  }

  const handleOptionItemClick = (target, index) => {
    let newValue = target.value;
    if (valueMode === ValueModeEnum.MultiSelect) {
      let currentValue = value;
      if (!Array.isArray(currentValue)) {
        currentValue = !currentValue ? [] : [currentValue];
      }

      newValue = addOrRemove(currentValue, target.value);
    }

    if (valueMode === ValueModeEnum.Range) {
      let incomingArray = [...(props.rulePair.GetValue() ?? [null, null])];
      incomingArray[index] = newValue;
      props.onUpdate("value")({
        value: incomingArray
      });
      return;
    }

    props.onUpdate("value")({ value: newValue });
  };

  const handleInputChange = (e, value, index) => {
    const nextValue = value ?? e.target.value;

    if (valueMode === ValueModeEnum.Range) {
      let incomingArray = [...(props.rulePair.GetValue() ?? [null, null])];
      incomingArray[index] = nextValue;
      props.onUpdate("value")({
        value: incomingArray
      });
      return;
    }
    if (valueMode !== ValueModeEnum.MultiSelect) {
      props.onUpdate("value")({ value: nextValue });
      return;
    }

    const inputValue = nextValue;
    const nextValues = inputValue.split(",");

    // When pressing backspace on the last value, we want to clear the empty string
    if (inputValue.charAt(inputValue.length - 1) === "," && e.nativeEvent.data !== ",") {
      nextValues.pop();
    }

    props.onUpdate("value")({ value: nextValues });
  };

  const handleInputBlur = () => {
    if (valueMode !== ValueModeEnum.MultiSelect) {
      return;
    }

    let currentValue = value;
    if (!Array.isArray(currentValue)) {
      currentValue = !currentValue ? [] : [currentValue];
    }

    const nextValues = currentValue.map(v => v.trim()).filter(v => v !== "");
    props.onUpdate("value")({ value: nextValues });
  };

  const disabled = loading || !field;
  const selectSize = props.selectSize ?? "small";

  const valueHasError = !!props.valueError?.hasError;

  const rangeValueErrorActive = valueHasError && valueMode === ValueModeEnum.Range;
  const startEmpty = value[0] == null || value[0] === "";
  const endEmpty = value[1] == null || value[1] === "";
  const rangeInvalidFields = rangeValueErrorActive && !startEmpty && !endEmpty;
  const startHasError = rangeValueErrorActive && (startEmpty || rangeInvalidFields);
  const endHasError = rangeValueErrorActive && (endEmpty || rangeInvalidFields);

  const dateRangeValueErrorActive = valueHasError && valueMode === ValueModeEnum.DateRange;
  const _dateRangeBoundNeedsValue = dateType =>
    !["today", "yesterday", "tomorrow", null].includes(dateType);
  const fromDateBoundMissing =
    dateRangeValueErrorActive &&
    _dateRangeBoundNeedsValue(value?.from?.dateType) &&
    (value?.from?.value == null || value?.from?.value === "");
  const toDateBoundMissing =
    dateRangeValueErrorActive &&
    _dateRangeBoundNeedsValue(value?.to?.dateType) &&
    (value?.to?.value == null || value?.to?.value === "");
  const dateRangeBothInvalid =
    dateRangeValueErrorActive && !fromDateBoundMissing && !toDateBoundMissing;
  const fromDateStatus = fromDateBoundMissing || dateRangeBothInvalid ? "error" : undefined;
  const toDateStatus = toDateBoundMissing || dateRangeBothInvalid ? "error" : undefined;

  const isDateRangeField =
    fieldType === enumFieldType.date && props.rulePair?.GetComparison() === "daterange";
  // TODO: Handle multiselect dates
  const isBlockedValue = isBlockedField(value) || value === getBlockedFieldDisplayValue();
  const InputComponent = isBlockedValue
    ? Input
    : isDateRangeField
      ? DateRangeComponentField
      : valueMode !== ValueModeEnum.MultiSelect
        ? (InputComponentDict[fieldType] ?? Input)
        : Input;
  const customInputProps = isBlockedValue
    ? {}
    : !isNullish(InputComponentDict[fieldType]) && valueMode !== ValueModeEnum.MultiSelect
      ? {
          customInput: Input,
          fieldDef: fieldDef,
          ...(isDateRangeField
            ? {
                afterLabelElem: props.afterLabelElem,
                fromStatus: fromDateStatus,
                toStatus: toDateStatus
              }
            : {})
        }
      : {};

  let dateTypeClass = "";
  if (isDateRangeField) dateTypeClass = "oc-date-range-wrapper";
  else if (props.rulePair?.GetComparison() === "range") dateTypeClass = "oc-number-range-wrapper";

  return (
    <div className={cx("oc-rule-pair-value-input-wrapper", dateTypeClass)}>
      {[ValueModeEnum.SingleSelect, ValueModeEnum.MultiSelect, ValueModeEnum.DateRange].includes(
        valueMode
      ) ? (
        <Flex
          gap={"8px"}
          alignSelf={"stretch"}
          flex={"1"}
          alignItems={"flex-end"}
          className="oc-range-value-content"
        >
          <div className={cx("oc-flex-1", "ocn-field-input-value-row")}>
            <InputComponent
              disabled={disabled}
              data-testid={`${props.dataTestIdPrefix}-val-input`}
              size={selectSize}
              type="text"
              onBlur={handleInputBlur}
              onChange={(e, value) => handleInputChange(e, value)}
              value={NormalizeFieldValue(value)}
              placeholder={
                disabled
                  ? appstr("enterval")
                  : valueMode !== ValueModeEnum.DateRange && !NormalizeFieldValue(value)
                    ? appstr("<blank>")
                    : undefined
              }
              status={!isDateRangeField && props.valueError?.hasError ? "error" : undefined}
              label={!isDateRangeField ? appstr("Value") : undefined}
              require={!isDateRangeField ? !!props.afterLabelElem : undefined}
              {...customInputProps}
            />
          </div>
          {valueMode !== ValueModeEnum.DateRange && (
            <Menu
              label={valueString}
              value={value}
              disabled={disabled}
              stayOpenOnOptionSelect={valueMode === ValueModeEnum.MultiSelect}
              position={"bottom-end"}
              listProps={FieldSelectListProps({
                customPlaceholder: appstr("Find") + " " + field || "",
                inputProps: { dataTestId: `${props.dataTestIdPrefix}-val--search` }
              })}
              options={(data || []).map(s => ({
                dataTestId: `${props.dataTestIdPrefix}-val--${s}`,
                label: FormatValueFromFieldName(field, s),
                value: s,
                clickHandler: handleOptionItemClick
              }))}
              trigger={({ ariaAttrs }) => (
                <Button
                  disabled={disabled}
                  dataTestId={`${props.dataTestIdPrefix}-val`}
                  iconOnly
                  iconProps={{
                    modifier: "white",
                    name: "angle-down",
                    title: "angle-down"
                  }}
                  modifier={"secondary"}
                  size={selectSize}
                  className={"oc-value-picker"}
                  {...ariaAttrs}
                />
              )}
              {...props.menuProps}
              popoverProps={{
                ...props.menuProps?.popoverProps,
                floatingProps: {
                  ...props.menuProps?.popoverProps?.floatingProps,
                  "data-testid": `${popoverTestIdPrefix}-val-popover`
                }
              }}
            />
          )}
        </Flex>
      ) : valueMode == ValueModeEnum.Range ? (
        <Flex
          gap={"8px"}
          flex={"1"}
          alignItems={"stretch"}
          className={cx("oc-number-range-input-field", "oc-range-value-content")}
        >
          <Flex flex={"1"} gap={"8px"} alignItems={"flex-end"}>
            <div className={cx("oc-flex-1", "ocn-field-input-value-row")}>
              <InputComponent
                disabled={disabled}
                data-testid={`${props.dataTestIdPrefix}-val-input`}
                size={selectSize}
                type="text"
                onBlur={handleInputBlur}
                onChange={(e, value) => handleInputChange(e, value, 0)}
                value={value[0]}
                placeholder={
                  disabled ? appstr("enterval") : value[0] == null ? appstr("<blank>") : undefined
                }
                status={startHasError ? "error" : undefined}
                aria-invalid={startHasError || undefined}
                label={appstr("Starting value")}
                require={!!props.afterLabelElem}
                noMargin
                showMessage={false}
                {...customInputProps}
              />
            </div>
            <Menu
              label={valueString}
              value={value[0]}
              disabled={disabled}
              position={"bottom-end"}
              listProps={FieldSelectListProps({
                customPlaceholder: appstr("Find") + " " + field || "",
                inputProps: { dataTestId: `${props.dataTestIdPrefix}-val--search` }
              })}
              options={(data || []).map(s => ({
                dataTestId: `${props.dataTestIdPrefix}-val--${s}`,
                label: FormatValueFromFieldName(field, s),
                value: s,
                clickHandler: e => handleOptionItemClick(e, 0)
              }))}
              trigger={({ ariaAttrs }) => (
                <Button
                  disabled={disabled}
                  dataTestId={`${props.dataTestIdPrefix}-val`}
                  iconOnly
                  iconProps={{
                    modifier: "white",
                    name: "angle-down",
                    title: "angle-down"
                  }}
                  modifier={"secondary"}
                  size={selectSize}
                  className={"oc-value-picker"}
                  {...ariaAttrs}
                />
              )}
              {...props.menuProps}
              popoverProps={{
                ...props.menuProps?.popoverProps,
                floatingProps: {
                  ...props.menuProps?.popoverProps?.floatingProps,
                  "data-testid": `${popoverTestIdPrefix}-val-popover`
                }
              }}
            />
          </Flex>

          <Flex flex={"1"} gap={"8px"} alignItems={"flex-end"}>
            <div className={cx("oc-flex-1", "ocn-field-input-value-row")}>
              <InputComponent
                disabled={disabled}
                data-testid={`${props.dataTestIdPrefix}-val-input`}
                size={selectSize}
                type="text"
                onBlur={handleInputBlur}
                onChange={(e, value) => handleInputChange(e, value, 1)}
                value={value[1]}
                placeholder={
                  disabled ? appstr("enterval") : value[1] == null ? appstr("<blank>") : undefined
                }
                status={endHasError ? "error" : undefined}
                aria-invalid={endHasError || undefined}
                label={appstr("Ending value")}
                require={!!props.afterLabelElem}
                noMargin
                showMessage={false}
                {...customInputProps}
              />
            </div>
            <Menu
              label={valueString}
              value={value[1]}
              disabled={disabled}
              position={"bottom-end"}
              listProps={FieldSelectListProps({
                customPlaceholder: appstr("Find") + " " + field || "",
                inputProps: { dataTestId: `${props.dataTestIdPrefix}-val--search` }
              })}
              options={(data || []).map(s => ({
                dataTestId: `${props.dataTestIdPrefix}-val--${s}`,
                label: FormatValueFromFieldName(field, s),
                value: s,
                clickHandler: e => handleOptionItemClick(e, 1)
              }))}
              trigger={({ ariaAttrs }) => (
                <Button
                  disabled={disabled}
                  dataTestId={`${props.dataTestIdPrefix}-val-2`}
                  iconOnly
                  iconProps={{
                    modifier: "white",
                    name: "angle-down",
                    title: "angle-down"
                  }}
                  modifier={"secondary"}
                  size={selectSize}
                  className={"oc-value-picker"}
                  {...ariaAttrs}
                />
              )}
              {...props.menuProps}
              popoverProps={{
                ...props.menuProps?.popoverProps,
                floatingProps: {
                  ...props.menuProps?.popoverProps?.floatingProps,
                  "data-testid": `${popoverTestIdPrefix}-val-2-popover`
                }
              }}
            />
          </Flex>
        </Flex>
      ) : (
        <Input
          data-testid={`${props.dataTestIdPrefix}-val-input`}
          size={selectSize}
          readOnly={!field}
          onChange={handleInputChange}
          value={value}
          placeholder={!field ? appstr("enterval") : !value ? appstr("<blank>") : undefined}
          status={props.valueError?.hasError ? "error" : undefined}
          label={appstr("Value")}
          require={!!props.afterLabelElem}
        />
      )}
    </div>
  );
};
